import { loadPickupPointsAction } from '../../../../actions/address/loadPickupPointsAction';

type PickupPointsPromise = ReturnType<typeof loadPickupPointsAction>;

export interface PickupPointsSearch {
  city: string;
  zip: string;
}

const pickupPointsBySearch = new Map<string, PickupPointsPromise>();
const failedSearchKeys = new Set<string>();

const normalizeSearch = ({ city, zip }: PickupPointsSearch): PickupPointsSearch => ({
  city: city.trim(),
  zip: zip.trim()
});

const getSearchKey = ({ city, zip }: PickupPointsSearch) => `${zip}|${city}`;

const cachePickupPoints = (key: string, promise: PickupPointsPromise) => {
  pickupPointsBySearch.set(key, promise);
  promise.then(
    result => {
      if (!result.success) {
        failedSearchKeys.add(key);
      }
    },
    () => failedSearchKeys.add(key)
  );
};

/**
 * Returns one stable promise per search, so `use()` can suspend on it without refetching on every render.
 * @param search - City and zip to look up pickup points for.
 * @returns The pickup points result promise.
 */
export function getPickupPoints(search: PickupPointsSearch): PickupPointsPromise {
  const normalizedSearch = normalizeSearch(search);
  const key = getSearchKey(normalizedSearch);
  const cached = pickupPointsBySearch.get(key);

  if (cached) {
    return cached;
  }

  const promise = loadPickupPointsAction(normalizedSearch);

  cachePickupPoints(key, promise);

  return promise;
}

/**
 * Puts the server-streamed result into the cache, so it is reused for its own search and evicted like any other
 * failed lookup. Call it from an effect: the cache is module-level and must not be filled during server rendering.
 * @param search - City and zip the promise was fetched for.
 * @param promise - Pickup points promise streamed from the server.
 */
export function seedPickupPoints(search: PickupPointsSearch, promise: PickupPointsPromise) {
  const key = getSearchKey(normalizeSearch(search));

  if (!pickupPointsBySearch.has(key)) {
    cachePickupPoints(key, promise);
  }
}

/**
 * Drops failed lookups so the next open retries them. Discarding right when a promise fails would make the
 * list that is still rendering it request a new one and loop, so this runs once the list is closed.
 */
export function discardFailedPickupPoints() {
  failedSearchKeys.forEach(key => pickupPointsBySearch.delete(key));
  failedSearchKeys.clear();
}

export function clearPickupPointsCache() {
  pickupPointsBySearch.clear();
  failedSearchKeys.clear();
}
